import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  SmartToy as ArtIcon,
  Psychology as PsychologyIcon,
  Storage as StorageIcon,
  DataObject as DataIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../services/firebase';

const API_BASE = '';

// All model usage points
const MODEL_CONFIG = [
  {
    key: 'model',
    label: 'Art (Chat Agent)',
    icon: <ArtIcon fontSize="small" color="primary" />,
    description: 'Main chat intelligence agent on the dashboard',
    type: 'generation',
  },
  {
    key: 'rag_model',
    label: 'RAG Engine',
    icon: <PsychologyIcon fontSize="small" color="secondary" />,
    description: 'Answer generation from retrieved documents',
    type: 'generation',
    note: 'Synced with Art model in BQ',
  },
  {
    key: 'agentic_model',
    label: 'RAG Agentic',
    icon: <PsychologyIcon fontSize="small" color="secondary" />,
    description: 'Function calling for iterative query loops',
    type: 'generation',
  },
  {
    key: 'embedding_model',
    label: 'Embedding Pipeline',
    icon: <StorageIcon fontSize="small" sx={{ color: '#00897b' }} />,
    description: 'Vector embeddings for SEC filings (768 dimensions)',
    type: 'embedding',
    warning: 'Changing this may require re-embedding all documents',
  },
  {
    key: 'financial_extractor_model',
    label: 'Financial Extractor',
    icon: <DataIcon fontSize="small" sx={{ color: '#ef6c00' }} />,
    description: 'Structured extraction from 10-Q/10-K financials',
    type: 'generation',
  },
  {
    key: 'executive_extractor_model',
    label: 'Executive Extractor',
    icon: <DataIcon fontSize="small" sx={{ color: '#ef6c00' }} />,
    description: 'Executive change extraction from 8-K Item 5.02',
    type: 'generation',
  },
];

function AgentSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState(null);
  const [edits, setEdits] = useState({}); // Track unsaved changes

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = await auth.currentUser?.getIdToken();
      const resp = await fetch(`${API_BASE}/api/agent/settings?refresh_models=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setSettings(data);
      setEdits({});
      setError(null);
    } catch (err) {
      setError(`Failed to load settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const getCurrentValue = (key) => {
    return edits[key] !== undefined ? edits[key] : (settings?.[key] || '');
  };

  const handleModelChange = (key, value) => {
    setEdits((prev) => {
      const next = { ...prev };
      if (value === settings?.[key]) {
        delete next[key]; // No change from server value
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const hasChanges = Object.keys(edits).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      setSaving(true);
      const token = await auth.currentUser?.getIdToken();
      const resp = await fetch(`${API_BASE}/api/agent/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(edits),
      });
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      setSuccess(true);
      await fetchSettings();
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings?.is_admin) {
    return <Alert severity="error">Admin access required.</Alert>;
  }

  const generationModels = settings.available_models || [];
  const embeddingModels = settings.available_embedding_models || [];

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SettingsIcon fontSize="large" color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Agent Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure AI models across the platform
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            size="small"
          >
            {saving ? 'Saving...' : `Save${hasChanges ? ` (${Object.keys(edits).length})` : ''}`}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Model Configuration Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          LLM Models
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          All AI models used across the platform. Changes are saved to BigQuery and take effect within 5 minutes.
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="22%">Service</TableCell>
                <TableCell width="35%">Model</TableCell>
                <TableCell width="43%">Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MODEL_CONFIG.map((cfg) => {
                const isEdited = edits[cfg.key] !== undefined;
                const modelList = cfg.type === 'embedding' ? embeddingModels : generationModels;
                return (
                  <TableRow key={cfg.key} hover sx={isEdited ? { bgcolor: 'action.selected' } : {}}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {cfg.icon}
                        <Typography variant="body2" fontWeight={500}>
                          {cfg.label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={getCurrentValue(cfg.key)}
                          onChange={(e) => handleModelChange(cfg.key, e.target.value)}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            ...(isEdited ? { border: '2px solid', borderColor: 'warning.main' } : {}),
                          }}
                        >
                          {modelList.map((m) => (
                            <MenuItem key={m} value={m} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {m}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {cfg.description}
                      </Typography>
                      {cfg.warning && isEdited && (
                        <Typography variant="caption" color="warning.main" display="block">
                          ⚠️ {cfg.warning}
                        </Typography>
                      )}
                      {cfg.note && (
                        <Typography variant="caption" color="info.main" display="block">
                          ℹ️ {cfg.note}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Guardrails */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Guardrails & Limits
        </Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell><Typography variant="body2" fontWeight={500}>Rate Limit</Typography></TableCell>
              <TableCell>{settings.rate_limit}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body2" fontWeight={500}>Max Query Rows</Typography></TableCell>
              <TableCell>{settings.max_query_rows?.toLocaleString()}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body2" fontWeight={500}>Max Bytes Billed</Typography></TableCell>
              <TableCell>{settings.max_bytes_billed_gb} GB per query</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body2" fontWeight={500}>RAG Top-K</Typography></TableCell>
              <TableCell>{settings.rag_top_k} documents</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body2" fontWeight={500}>Scope</Typography></TableCell>
              <TableCell>Biotech / pharmaceutical analysis only</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body2" fontWeight={500}>BQ Access</Typography></TableCell>
              <TableCell>Read-only (SELECT only, 3 datasets)</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message="Settings saved — changes take effect within 5 minutes"
      />
    </Box>
  );
}

export default AgentSettings;
